import { useState, useEffect, useRef, useCallback } from 'react'
import { serverURL } from '../api/config'

type SSEStatus = 'idle' | 'connecting' | 'starting' | 'queued' | 'running' | 'completed' | 'error'

export type SSEType = 'scan' | 'application' | 'upload' | 'profile'

const ENDPOINT_PREFIX: Record<SSEType, string> = {
  scan: 'scans',
  application: 'applications',
  upload: 'orgs/uploads',
  profile: 'orgs/profiles',
}

// Lightweight, non-streaming status poll used to re-sync after an SSE drop. The
// scan and application routes differ (the application `/{org_uuid}` route forces
// a `/status/{id}` prefix), so build each explicitly.
const STATUS_URL: Record<SSEType, (jobId: string) => string> = {
  scan: (jobId) => `${serverURL}/scans/${jobId}/status`,
  application: (jobId) => `${serverURL}/applications/status/${jobId}`,
  upload: (jobId) => `${serverURL}/orgs/uploads/${jobId}/status`,
  profile: (jobId) => `${serverURL}/orgs/profiles/${jobId}/status`,
}

// Reconnect backoff after a transient drop while the job is still running.
const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30000

interface UseSSEProgressOptions {
  type: SSEType
  jobId: string | null
  token: string | null
  onComplete?: () => void
  onError?: (message: string) => void
}

export function useSSEProgress({ type, jobId, token, onComplete, onError }: UseSSEProgressOptions) {
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState<SSEStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<any | null>(null)

  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const eventSourceRef = useRef<EventSource | null>(null)
  const completedRef = useRef(false)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const completionResyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  // Bumped whenever the tracked job (jobId/token) changes or the hook unmounts.
  // An in-flight async resync captures the generation at call time and bails if it
  // changed, so a slow status fetch can never fire callbacks for a superseded job.
  const generationRef = useRef(0)

  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (completionResyncTimerRef.current) {
      clearTimeout(completionResyncTimerRef.current)
      completionResyncTimerRef.current = null
    }
  }, [])

  const finishComplete = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    cleanup()
    setStatus('completed')
    setProgress(100)
    onCompleteRef.current?.()
  }, [cleanup])

  const finishError = useCallback(
    (message: string) => {
      // Terminal + idempotent, mirroring finishComplete: once we've reported a
      // terminal outcome (error OR complete) we must not fire another callback,
      // and an error must block a later stray completion.
      if (completedRef.current) return
      completedRef.current = true
      cleanup()
      setStatus('error')
      setError(message)
      onErrorRef.current?.(message)
    },
    [cleanup]
  )

  // Authoritative re-sync: ask the API for the job's current status. Returns the
  // terminal outcome, 'running' if still in progress, or null if the status could
  // not be fetched. Unlike the live SSE stream this survives the connection being
  // dropped (laptop sleep / refresh) — the job may have finished while we were
  // disconnected.
  const resync = useCallback(async (): Promise<'completed' | 'error' | 'running' | null> => {
    if (!jobId || !token) return null
    const myGeneration = generationRef.current
    try {
      const res = await fetch(STATUS_URL[type](jobId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return null
      const data = await res.json()
      // The tracked job changed (or the hook unmounted) while we were awaiting
      // (fetch OR json parse) — abandon without touching state or firing callbacks
      // for a stale job. Checked after the LAST await, before any state/callback.
      if (myGeneration !== generationRef.current) return null
      setData(data)
      if (data.progress !== undefined) {
        const nextProgress = Number(data.progress)
        setProgress((current) => Math.max(current, nextProgress))
      }
      if (data.status === 'completed') {
        finishComplete()
        return 'completed'
      }
      if (data.status === 'error' || data.status === 'cleaned_up') {
        finishError(data.error || 'Job failed')
        return 'error'
      }
      return 'running'
    } catch {
      return null
    }
  }, [jobId, token, type, finishComplete, finishError])

  const connect = useCallback(() => {
    if (!jobId || !token) return

    cleanup()
    completedRef.current = false
    setStatus('connecting')
    setError(null)
    setData(null)

    const prefix = ENDPOINT_PREFIX[type]
    const sseUrl = new URL(`${serverURL}/${prefix}/progress/${jobId}`)
    // NOTE: Token is passed as a query parameter because EventSource does not support
    // custom headers. The server should validate token expiry and use short-lived tokens.
    sseUrl.searchParams.set('token', token)

    const es = new EventSource(sseUrl.toString(), { withCredentials: true })
    eventSourceRef.current = es

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        // A real message means the connection is healthy — reset backoff.
        reconnectAttemptsRef.current = 0

        if (data.status) setStatus(data.status as SSEStatus)
        if (data.progress !== undefined) {
          const nextProgress = Number(data.progress)
          setProgress((current) => Math.max(current, nextProgress))
        }
        if (data.error) setError(data.error)
        setData(data)

        if (data.status === 'completed') {
          finishComplete()
        } else if (data.status === 'error' || data.status === 'cleaned_up') {
          finishError(data.error || data.message || 'Job failed')
        } else if (Number(data.progress) >= 100 && !completionResyncTimerRef.current) {
          const myGeneration = generationRef.current
          completionResyncTimerRef.current = setTimeout(async () => {
            completionResyncTimerRef.current = null
            if (myGeneration !== generationRef.current || completedRef.current) return
            await resync()
          }, 1000)
        }
      } catch {
        // Ignore parse errors from keepalive comments
      }
    }

    es.onerror = async () => {
      if (completedRef.current) return
      const myGeneration = generationRef.current
      cleanup()

      // The stream dropped (laptop sleep, refresh, proxy/network blip). Do NOT
      // assume failure — re-sync first. The job may have completed while we were
      // disconnected (issue #1295: scan reached 100% during sleep, no Continue).
      const outcome = await resync()
      // The tracked job changed (or unmounted) while we were re-syncing — do not
      // fire an error or schedule a reconnect for a superseded job.
      if (myGeneration !== generationRef.current) return
      if (outcome === 'completed' || outcome === 'error') return

      // Still running (or status unavailable because the API is restarting):
      // reconnect with capped backoff. Reconnecting replays missed progress from
      // the Redis event stream once the API is reachable again, so transport
      // loss is not a terminal job error.
      reconnectAttemptsRef.current += 1
      const delay = Math.min(
        RECONNECT_BASE_MS * 2 ** (reconnectAttemptsRef.current - 1),
        RECONNECT_MAX_MS
      )
      setStatus('connecting')
      // cleanup() already cancels this timer on a job switch/unmount, but also
      // guard at fire time so a scheduled reconnect can never re-open a stream for
      // a superseded job even if the cancellation path is ever refactored away.
      reconnectTimerRef.current = setTimeout(() => {
        if (myGeneration !== generationRef.current) return
        connect()
      }, delay)
    }
  }, [jobId, token, type, cleanup, resync, finishComplete, finishError])

  useEffect(() => {
    if (!jobId || !token) return
    // New tracked job: invalidate any in-flight resync from a prior job.
    generationRef.current += 1
    completedRef.current = false
    reconnectAttemptsRef.current = 0
    setProgress(0)
    setStatus('connecting')
    setError(null)
    setData(null)
    // Re-sync once on mount before opening the stream, so a job that already
    // finished (e.g. completed before this component mounted) resolves immediately
    // instead of waiting on the stream.
    let cancelled = false
    resync().then((outcome) => {
      if (cancelled || outcome === 'completed' || outcome === 'error') return
      connect()
    })
    return () => {
      cancelled = true
      generationRef.current += 1
      cleanup()
    }
  }, [jobId, token, resync, connect, cleanup])

  const retry = useCallback(() => {
    reconnectAttemptsRef.current = 0
    connect()
  }, [connect])

  return { progress, status, error, data, retry }
}
